import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { lizardDateSchema } from "../dates";
import { polygonSchema } from "../geojson";
import { paginatedSchema } from "../pagination";

import { addressSchema } from "./addresses";
import { baseAssetSchema } from "./assetsBase";

export const buildingListItemSchema = baseAssetSchema.extend({
  geometry: polygonSchema,
  addresses: z.array(addressSchema),
  start: lizardDateSchema,
  end: lizardDateSchema.nullable(),
  build_year: z.number().nullable(),
});

export type BuildingListItem = z.infer<typeof buildingListItemSchema>;

export const buildingListSchema = paginatedSchema(buildingListItemSchema);
export type BuildingList = z.infer<typeof buildingListSchema>;

export const buildingSchema = buildingListItemSchema;
export type Building = z.infer<typeof buildingSchema>;

export function useBuildings(params = {}) {
  const url = combineUrlAndParams("/api/v4/buildings/", params);
  return useParsedQuery<BuildingList>(
    ["lizard-api-v4", "buildings", url],
    () => fetchWithError(url),
    (json: unknown) => buildingListSchema.parse(json)
  );
}

export function useBuilding(id: number) {
  return useParsedQuery<Building>(
    ["lizard-api-v4", "buildings", id],
    () => fetchWithError(`/api/v4/buildings/${id}/`),
    (json: unknown) => buildingSchema.parse(json)
  );
}

export function useMultipleBuildings(ids: number[]) {
  return useParsedQueries(
    ids.map((id) => {
      return {
        queryKey: ["lizard-api-v4", "buildings", id],
        fetchFunction: () => fetchWithError(`/api/v4/buildings/${id}/`),
        parseFunction: (json: unknown) => buildingSchema.parse(json),
      };
    })
  );
}
